import { useState, useEffect } from "react";
import { walletApi } from "@/lib/api";
import WalletTopupDialog from "./WalletTopupDialog";
import { toast } from "sonner";
import { Wallet, ArrowsClockwise, Plus, ToggleLeft, ToggleRight, Receipt } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function WalletCard() {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupOpen, setTopupOpen] = useState(false);
  const [sweepBusy, setSweepBusy] = useState(false);

  const fetchWallet = () => {
    walletApi.me()
      .then(({ data }) => { setWallet(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchWallet();
  }, []);

  const handleToggleAutoPay = async () => {
    if (!wallet) return;
    const next = !wallet.autoPayEnabled;
    try {
      await walletApi.toggleAutoPay(next);
      setWallet({ ...wallet, autoPayEnabled: next });
      toast.success(next ? "Auto-pay enabled — new bereavements will be paid from your wallet." : "Auto-pay turned off.");
    } catch { toast.error("Could not update auto-pay"); }
  };

  const handleSweep = async () => {
    if (!wallet) return;
    setSweepBusy(true);
    try {
      const { data } = await walletApi.sweep();
      const paidN = (data.paid || []).length;
      const skippedN = (data.skipped || []).length;
      if (paidN === 0 && skippedN === 0) toast.info("No unpaid bereavements found.");
      else if (paidN > 0) toast.success(`Cleared ${paidN} bereavement(s) from your wallet. Balance: KES ${Math.round(data.balance).toLocaleString()}`);
      else toast.warning(`Could not sweep — ${skippedN} bereavement(s) skipped (insufficient balance).`);
      fetchWallet();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sweep failed"); }
    finally { setSweepBusy(false); }
  };

  if (loading) {
    return <div className="bg-card border border-border rounded-xl p-5 animate-pulse h-32" data-testid="wallet-card-loading" />;
  }
  if (!wallet) return null;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-200 rounded-xl p-5 space-y-4" data-testid="wallet-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center">
            <Wallet className="w-5 h-5" weight="fill" />
          </div>
          <div>
            <p className="font-heading font-semibold text-sm">My Wallet</p>
            <p className="text-[11px] text-muted-foreground">Prepay bereavement contributions</p>
          </div>
        </div>
        <Link to="/dashboard/wallet" data-testid="wallet-statement-link" className="text-xs text-emerald-700 hover:underline flex items-center gap-1">
          <Receipt className="w-3.5 h-3.5" /> Statement
        </Link>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Balance</p>
          <p className="font-heading text-3xl font-bold text-emerald-700" data-testid="wallet-balance">KES {Math.round(wallet.balance).toLocaleString()}</p>
        </div>
        <button
          onClick={() => setTopupOpen(true)}
          data-testid="wallet-topup-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold active:scale-95"
        >
          <Plus className="w-4 h-4" weight="bold" /> Top up
        </button>
      </div>

      <div className="border-t border-emerald-200 pt-3 flex items-center justify-between gap-3">
        <div className="flex-1">
          <p className="text-xs font-semibold">Auto-pay new bereavements</p>
          <p className="text-[11px] text-muted-foreground">When ON, new posts are paid from your wallet automatically.</p>
        </div>
        <button
          onClick={handleToggleAutoPay}
          data-testid="wallet-autopay-toggle"
          aria-label="Toggle auto-pay"
          className="text-emerald-700"
        >
          {wallet.autoPayEnabled
            ? <ToggleRight className="w-9 h-9" weight="fill" />
            : <ToggleLeft className="w-9 h-9" />
          }
        </button>
      </div>

      <button
        onClick={handleSweep}
        disabled={sweepBusy || wallet.balance <= 0}
        data-testid="wallet-sweep-btn"
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-emerald-300 text-emerald-700 text-xs font-semibold active:scale-95 disabled:opacity-50"
      >
        <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> {sweepBusy ? "Sweeping…" : "Sweep arrears now"}
      </button>

      <WalletTopupDialog open={topupOpen} onClose={() => setTopupOpen(false)} onCredited={fetchWallet} />
    </div>
  );
}
